import { Injectable } from '@angular/core';
import { Role } from '../interfaces/role.interface';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RolesService {
  constructor(private afs: AngularFirestore) {}

  getRoles(): Observable<Role[]> {
    return this.afs.collection('utils').doc('userTypes')
      .valueChanges()
      .pipe(
        map((doc: any) => doc?.userTypes || [])
      );
  }

  async saveAllRoles(roles: Role[]): Promise<void> {
    // Sanitize roles to remove any undefined values which Firestore rejects
    const sanitizedRoles = roles.map(role => {
      // Create a clean copy with only valid properties
      const cleanRole: Role = {
        name: role.name,
        permissions: [...(role.permissions || [])]
      };

      // Add optional properties only if they're defined
      if (role.canEdit !== undefined) cleanRole.canEdit = role.canEdit;
      if (role.disabled !== undefined) cleanRole.disabled = role.disabled;
      if (role.id !== undefined) cleanRole.id = role.id;
      if (role.description !== undefined) cleanRole.description = role.description;

      // Handle EPP tab permissions
      if (role.eppTabPermissions) {
        cleanRole.eppTabPermissions = { ...role.eppTabPermissions };
      }

      // Handle granular permissions
      if (role.granularPermissions && role.granularPermissions.length > 0) {
        cleanRole.granularPermissions = role.granularPermissions.map(gp => ({
          module: gp.module,
          permissions: [...gp.permissions]
        }));
      }

      return cleanRole;
    });

    await this.afs.collection('utils').doc('userTypes').set({ userTypes: sanitizedRoles }, { merge: true });

    const hasAllPermission = (role: Role): boolean => role.permissions?.includes('ALL') ?? false;
    const rrhhGranularPermissions = (role: Role): string[] => {
      const gp = role.granularPermissions?.find((p) => p.module === 'rrhh');
      return gp?.permissions || [];
    };
    const hasAnyRrhhAccess = (role: Role): boolean =>
      hasAllPermission(role)
      || role.permissions?.some((p) => typeof p === 'string' && p.startsWith('/rrhh')) === true
      || rrhhGranularPermissions(role).length > 0;
    const hasRrhhWrite = (role: Role): boolean =>
      hasAllPermission(role)
      || rrhhGranularPermissions(role).some((perm) => !perm.endsWith('.ver'));
    const hasRrhhDelete = (role: Role): boolean =>
      hasAllPermission(role)
      || rrhhGranularPermissions(role).some((perm) => perm.endsWith('.eliminar'));

    const rrhhReadAllowedRoles = sanitizedRoles.filter(hasAnyRrhhAccess).map((role) => role.name);
    const rrhhWriteAllowedRoles = sanitizedRoles.filter(hasRrhhWrite).map((role) => role.name);
    const rrhhDeleteAllowedRoles = sanitizedRoles.filter(hasRrhhDelete).map((role) => role.name);
    const legacyUtilsWriters = ['Administrador', 'SysAdmin', 'Admin operativo', 'Admin administrativo'];
    const utilsWriteAllowedRoles = sanitizedRoles
      .filter((role) => legacyUtilsWriters.includes(role.name) || hasAllPermission(role))
      .map((role) => role.name);

    await this.afs.collection('utils').doc('rrhh_read_allowed_roles').set({ allowedRoles: rrhhReadAllowedRoles }, { merge: true });
    await this.afs.collection('utils').doc('rrhh_write_allowed_roles').set({ allowedRoles: rrhhWriteAllowedRoles }, { merge: true });
    await this.afs.collection('utils').doc('rrhh_delete_allowed_roles').set({ allowedRoles: rrhhDeleteAllowedRoles }, { merge: true });
    await this.afs.collection('utils').doc('utils_write_allowed_roles').set({ allowedRoles: utilsWriteAllowedRoles }, { merge: true });

    // Derivado para reglas: roles con permiso granular auditoria-archivos.ver → rrhh_audit_log (lectura).
    // Las reglas comparan allowedRoles con users/{uid}.role(s).
    const rrhhAuditLogAllowedRoles = sanitizedRoles
      .filter(
        (role) =>
          hasAllPermission(role)
          || role.granularPermissions?.some(
            (gp) => gp.module === 'rrhh' && (gp.permissions || []).includes('auditoria-archivos.ver')
          ) === true
      )
      .map((role) => role.name);

    await this.afs
      .collection('utils')
      .doc('rrhh_audit_log_allowed_roles')
      .set({ allowedRoles: rrhhAuditLogAllowedRoles }, { merge: true });
  }

  async initializeDefaultRoles(): Promise<void> {
    const defaultRoles: Role[] = [
      {
        name: "SysAdmin",
        disabled: true,
        permissions: ["ALL"]
      },
      {
        name: "Administrador",
        canEdit: true,
        permissions: ["/clientes", "/personas", "/eventos", "/viandas", "/tareas"]
      },
      {
        name: "Admin operativo",
        canEdit: true,
        permissions: []
      },
      {
        name: "Admin administrativo",
        canEdit: true,
        permissions: []
      },
      {
        name: "Cocina",
        canEdit: true,
        permissions: []
      },
      {
        name: "Campo",
        canEdit: true,
        permissions: []
      },
      {
        name: "Ventas",
        canEdit: true,
        permissions: []
      },
      {
        name: "Administración",
        canEdit: true,
        permissions: []
      },
      {
        name: "Eventos",
        canEdit: true,
        permissions: []
      },
      {
        name: "Usuario Final",
        canEdit: true,
        permissions: []
      },
      {
        name: "user",
        canEdit: true,
        permissions: []
      },
      {
        name: "AdminClient",
        canEdit: true,
        permissions: []
      },
      {
        name: "Delivery",
        canEdit: true,
        permissions: []
      },
      {
        name: "prueba editado",
        canEdit: true,
        permissions: []
      },
      {
        name: "prueba",
        canEdit: true,
        permissions: []
      },
      {
        name: "Almacenero",
        canEdit: true,
        permissions: [
          "/rrhh",
          "/rrhh/inicio",
          "/rrhh/gestion/empleados/epp"
        ],
        description: "Gestión de selección de EPP en almacén",
        eppTabPermissions: {
          allowedTabs: [2], // Solo "Selección en almacén"
          canViewStock: false,
          canEditStock: false,
          canSelectProducts: true,
          canConfirmSelection: true,
          canApproveDelivery: false,
          canDeliverProducts: false,
          canVerifyDelivery: false
        }
      },
      {
        name: "Operador RRHH",
        canEdit: true,
        permissions: [
          "/rrhh",
          "/rrhh/inicio",
          "/rrhh/gestion/empleados/nomina",
          "/rrhh/gestion/empleados/epp",
          "/rrhh/gestion/empleados/novedades",
          "/rrhh/gestion/empleados/control-horario",
          "/rrhh/gestion/empleados/capacitaciones",
          "/rrhh/gestion/empleados/vacaciones",
          "/rrhh/gestion/empleados/solicitudes",
          "/rrhh/administracion/organizacion",
          "/rrhh/administracion/configuracion"
        ],
        description: "Operaciones generales de RRHH sin aprobación de entregas",
        eppTabPermissions: {
          deniedTabs: [1], // NO puede ver "Aprobación de entrega"
          canViewStock: true,
          canEditStock: true,
          canSelectProducts: true,
          canConfirmSelection: true,
          canApproveDelivery: false,
          canDeliverProducts: true,
          canVerifyDelivery: true
        }
      }
    ];

    await this.saveAllRoles(defaultRoles);
  }
}
